import React, { forwardRef, useState, useEffect } from "react";
import { TextField } from "@mui/material";
import { styled } from "@mui/material/styles";

type Props = {
  type: string;
  title?: string;
  style?: React.CSSProperties;
  validator?: (value: string) => string;
  disabled?: boolean;
  value?: string;
  required?: boolean;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
};

const primaryColor = "#292744";

const CustomTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: 12,
    backgroundColor: "#f7f8fa",
    fontSize: "var(--font-size-medium)",
    fontFamily: "var(--font-family-main)",
    "& fieldset": {
      borderColor: theme.palette.grey[300],
    },
    "&:hover fieldset": {
      borderColor: primaryColor,
      borderWidth: 1.5,
    },
    "&.Mui-focused fieldset": {
      borderColor: primaryColor,
      borderWidth: 1.5,
    },
  },
  "& input[type='date']": {
    padding: "14px",
    fontSize: "var(--font-size-medium)",
    fontFamily: "var(--font-family-main)",
    color: "#222",
    // Remove custom filter for calendar picker indicator to ensure visibility on mobile
    // "&::-webkit-calendar-picker-indicator": {
    //   cursor: "pointer",
    //   filter: "invert(40%) sepia(20%) saturate(200%) hue-rotate(200deg)",
    // },
  },
  "& .MuiInputLabel-root": {
    fontWeight: 500,
    fontSize: "var(--font-size-medium)",
    color: primaryColor,
  },
  "& .MuiInputLabel-root.Mui-required .MuiInputLabel-asterisk": {
    color: theme.palette.error.main,
  },
}));

const InputTextDate = forwardRef<HTMLInputElement, Props>(
  ({ type, title, style, validator, disabled, value = "", required, onChange }, ref) => {
    const [errorMsg, setErrorMsg] = useState("");

    useEffect(() => {
      if (validator) {
        setErrorMsg(validator(value));
      }
    }, [value, validator]);

    return (
      <CustomTextField
        label={title}
        type={type}
        fullWidth
        required={required}
        value={value}
        onChange={(e: any) => {
          onChange?.(e);
          if (validator) {
            setErrorMsg(validator(e.target.value));
          }
        }}
        onBlur={() => {
          if (validator) {
            setErrorMsg(validator(value));
          }
        }}
        error={!!errorMsg}
        helperText={errorMsg}
        inputRef={ref}
        disabled={disabled}
        sx={style}
        InputLabelProps={{
          shrink: true,
          style: {
            color: "#555",
            fontFamily: "var(--font-family-main)",
            fontSize: "var(--font-size-medium)",
          },
        }}
        InputProps={{
          style: {
            color: "#222",
            fontFamily: "var(--font-family-main)",
            fontSize: "var(--font-size-medium)",
          },
        }}
      />
    );
  }
);

export default InputTextDate;
